import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, User, ShoppingBag, ChevronDown, Menu, X, Home, Sparkles, Package, Layers, BookOpen, Heart, Gift } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useStore } from "@/lib/store";
import { useAuth } from "@/lib/auth";
import { CURRENCY_OPTIONS } from "@/lib/config";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from "@/components/ui/dropdown-menu";

const MENU_LINKS = [
  { to: "/", label: "Home", icon: Home },
  { to: "/shop", label: "Shop All Fragrances", icon: Package },
  { to: "/quiz", label: "Scent Quiz", icon: Sparkles },
  { to: "/discovery-builder", label: "Discovery Set Builder", icon: Layers },
  { to: "/gift-cards", label: "Gift Cards", icon: Gift },
  { to: "/notes", label: "Notes Guide", icon: BookOpen },
  { to: "/account", label: "My Account / Login", icon: User },
];

const MENU_SECONDARY = [
  { to: "/account", label: "Scentodore Society Loyalty", icon: Heart },
  { to: "/account", label: "Order Tracking", icon: Package },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { cart, setCartOpen, setSearchOpen, state, setCurrency } = useStore();
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setSearchOpen]);

  // Close menu when route changes
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const itemCount = (cart?.lines || []).reduce((n, l) => n + (l.quantity || 0), 0);

  return (
    <motion.header
      data-testid="global-header"
      className={`sticky top-0 z-40 sd-frosted border-b border-black/10 transition-all duration-300`}
      style={{ backgroundColor: "hsl(var(--sd-ivory) / 0.75)" }}
    >
      <div className={`max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between transition-all duration-300 ${scrolled ? "h-[60px]" : "h-[80px]"}`}>
        {/* Left: hamburger + wordmark */}
        <div className="flex items-center gap-3 sm:gap-4">
          <Button
            data-testid="header-hamburger-button"
            variant="ghost"
            size="icon"
            className="text-[#1A1918] hover:bg-black/5 -ml-2"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Link to="/" data-testid="header-logo" className="font-serif text-2xl sm:text-[26px] tracking-[0.14em] uppercase text-[#1A1918]">
            Scentodore
          </Link>
        </div>

        {/* Center: nav (desktop only) */}
        <nav className="hidden md:flex items-center gap-8 text-sm tracking-[0.14em] uppercase text-[#1A1918]/85">
          <Link to="/shop" data-testid="nav-shop" className={`hover:text-[#C5A059] transition-colors ${location.pathname.startsWith("/shop") ? "text-[#C5A059]" : ""}`}>Shop</Link>
          <Link to="/quiz" data-testid="nav-quiz" className={`hover:text-[#C5A059] transition-colors ${location.pathname.startsWith("/quiz") ? "text-[#C5A059]" : ""}`}>Scent Quiz</Link>
          <Link to="/discovery-builder" data-testid="nav-discovery" className={`hover:text-[#C5A059] transition-colors ${location.pathname.startsWith("/discovery") ? "text-[#C5A059]" : ""}`}>Discovery Set</Link>
          <Link to="/gift-cards" data-testid="nav-gift-cards" className={`hover:text-[#C5A059] transition-colors ${location.pathname.startsWith("/gift-cards") ? "text-[#C5A059]" : ""}`}>Gift Cards</Link>
          <Link to="/notes" data-testid="nav-notes" className="hover:text-[#C5A059] transition-colors">Notes</Link>
        </nav>

        {/* Right: search, currency, account, cart */}
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            data-testid="header-search-open-button"
            variant="ghost"
            size="icon"
            className="text-[#1A1918]"
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
          >
            <Search className="h-[18px] w-[18px]" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="header-currency-switcher"
                variant="ghost"
                className="hidden sm:inline-flex text-[12px] tracking-[0.14em] uppercase text-[#1A1918] px-2"
              >
                {state.currency}
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#FAF8F5] border-black/10" data-testid="currency-menu">
              {CURRENCY_OPTIONS.map((c) => (
                <DropdownMenuItem
                  key={c.code}
                  data-testid={`currency-option-${c.code}`}
                  onClick={() => setCurrency(c.code)}
                  className={`cursor-pointer ${state.currency === c.code ? "text-[#C5A059]" : ""}`}
                >
                  {c.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            data-testid="header-account-button"
            variant="ghost"
            size="icon"
            className="text-[#1A1918] hidden sm:inline-flex"
            aria-label={isAuthenticated ? "Account" : "Sign in"}
            onClick={() => navigate(isAuthenticated ? "/account" : "/login")}
          >
            <User className="h-[18px] w-[18px]" />
          </Button>

          <Button
            data-testid="header-cart-open-button"
            variant="ghost"
            className="text-[#1A1918] relative"
            onClick={() => setCartOpen(true)}
            aria-label={`Cart, ${itemCount} items`}
          >
            <ShoppingBag className="h-[18px] w-[18px]" />
            {itemCount > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-[#C5A059] text-[#1A1918] rounded-full text-[10px] w-[18px] h-[18px] flex items-center justify-center font-medium">
                {itemCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Hamburger Menu Drawer */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent
          side="left"
          data-testid="hamburger-menu-drawer"
          className="bg-[#FAF8F5] w-full sm:max-w-[420px] p-0 border-r border-black/10 flex flex-col"
        >
          <SheetHeader className="px-8 pt-8 pb-6 border-b border-black/10">
            <SheetTitle className="font-serif text-3xl tracking-[0.02em] text-left">Menu</SheetTitle>
            <div className="text-xs uppercase tracking-[0.18em] text-[#C5A059] text-left">Scentodore</div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            <nav className="px-4 py-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#1A1918]/50 px-4 mb-2">Explore</div>
              <ul className="space-y-1">
                {MENU_LINKS.map((link, idx) => {
                  const Icon = link.icon;
                  const isActive = link.to === "/" ? location.pathname === "/" : location.pathname.startsWith(link.to);
                  // Special-case account/login label
                  let label = link.label;
                  let to = link.to;
                  if (link.to === "/account") {
                    if (isAuthenticated) {
                      label = `Account (${user?.full_name?.split(" ")[0] || "You"})`;
                    } else {
                      label = "Sign In / Register";
                      to = "/login";
                    }
                  }
                  return (
                    <li key={`${link.label}-${idx}`}>
                      <Link
                        to={to}
                        data-testid={`menu-link-${link.label.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                        onClick={() => setMenuOpen(false)}
                        className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-colors group ${
                          isActive
                            ? "bg-[#C5A059]/15 text-[#1A1918]"
                            : "text-[#1A1918]/85 hover:bg-[#F1ECE4]"
                        }`}
                      >
                        <Icon className={`h-[18px] w-[18px] ${isActive ? "text-[#C5A059]" : "text-[#1A1918]/60 group-hover:text-[#C5A059]"} transition-colors`} />
                        <span className="font-serif text-xl">{label}</span>
                      </Link>
                    </li>
                  );
                })}
                {isAuthenticated && (
                  <li>
                    <button
                      data-testid="menu-logout-button"
                      onClick={() => { logout(); setMenuOpen(false); navigate("/"); }}
                      className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-[#1A1918]/70 hover:bg-[#F1ECE4] transition-colors group"
                    >
                      <User className="h-4 w-4 text-[#1A1918]/60 group-hover:text-[#C5A059]" />
                      <span className="text-sm">Sign out</span>
                    </button>
                  </li>
                )}
              </ul>

              <div className="text-[10px] uppercase tracking-[0.2em] text-[#1A1918]/50 px-4 mt-8 mb-2">Society</div>
              <ul className="space-y-1">
                {MENU_SECONDARY.map((link, idx) => {
                  const Icon = link.icon;
                  return (
                    <li key={`sec-${idx}`}>
                      <Link
                        to={link.to}
                        data-testid={`menu-secondary-${idx}`}
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-4 px-4 py-3 rounded-xl text-[#1A1918]/85 hover:bg-[#F1ECE4] transition-colors group"
                      >
                        <Icon className="h-4 w-4 text-[#1A1918]/60 group-hover:text-[#C5A059] transition-colors" />
                        <span className="text-sm">{link.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          {/* Currency at the bottom */}
          <div className="border-t border-black/10 px-8 py-5 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#1A1918]/50">Currency</div>
            <div className="flex flex-wrap gap-1.5" data-testid="menu-currency-options">
              {CURRENCY_OPTIONS.map((c) => (
                <button
                  key={c.code}
                  data-testid={`menu-currency-${c.code}`}
                  onClick={() => setCurrency(c.code)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    state.currency === c.code
                      ? "bg-[#C5A059]/15 border-[#C5A059] text-[#1A1918]"
                      : "bg-[#FAF8F5] border-black/10 text-[#1A1918]/75 hover:border-[#C5A059]/40"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="pt-2 text-[11px] text-[#1A1918]/50 tracking-wide">Small-batch · Hand-blended · Extrait strength</div>
          </div>
        </SheetContent>
      </Sheet>
    </motion.header>
  );
}

export default Header;
